<script setup lang="ts">
import type { User } from '@/types'

import { Form, Head, usePage } from '@inertiajs/vue3'
import SettingsCard from '@/components/settings/SettingsCard.vue'
import SettingsPanel from '@/components/settings/SettingsPanel.vue'
import { firstFormError } from '@/lib/forms'
import { dashboardLayout } from '@/lib/layouts'
import { adminSettingsProfilePath } from '@/routes'

defineOptions({
  layout: dashboardLayout,
})
const page = usePage()
const user = page.props.auth.user as User
</script>

<template>
  <Head title="Profile settings" />

  <SettingsPanel
    title="Settings"
    description="Manage profile, security, sessions, and appearance preferences."
  >
    <SettingsCard
      title="Profile information"
      description="Update the name shown across your account."
      icon="i-lucide-user"
    >
      <Form
        v-slot="{ errors, processing, recentlySuccessful }"
        method="patch"
        :action="adminSettingsProfilePath()"
        :options="{ preserveScroll: true }"
        class="max-w-2xl space-y-5"
      >
        <UFormField
          label="Name"
          name="name"
          :error="firstFormError(errors.name)"
          required
        >
          <UInput
            id="name"
            name="name"
            :default-value="user.name"
            autocomplete="name"
            placeholder="Full name"
            icon="i-lucide-user"
            class="w-full"
          />
        </UFormField>

        <div class="flex items-center gap-3">
          <UButton
            type="submit"
            label="Save changes"
            icon="i-lucide-save"
            :loading="processing"
          />

          <Transition
            enter-active-class="transition ease-in-out"
            enter-from-class="opacity-0"
            leave-active-class="transition ease-in-out"
            leave-to-class="opacity-0"
          >
            <UBadge
              v-show="recentlySuccessful"
              color="success"
              variant="subtle"
              icon="i-lucide-check"
            >
              Saved
            </UBadge>
          </Transition>
        </div>
      </Form>
    </SettingsCard>
  </SettingsPanel>
</template>
